import { useContext, useEffect, createContext } from 'react'
import { useStateWithMerge } from 'hooks'
import API from 'config/api'
import { CONFIG_CATEGORIES, CONFIG_KEYS } from 'utils/configurations'

const ConfigContext = createContext(null)

interface ConfigurationResponse {
  unit: {
    id: number
    name: string
    divisionId: number
    corporationId: number
  }
  configurations: {
    [category: string]: {
      [key: string]: unknown
    }
  }
}

function isConfigurationResponse(data: unknown): data is ConfigurationResponse {
  return (
    typeof data === `object` &&
    data !== null &&
    `unit` in data &&
    `configurations` in data
  )
}

interface ConfigState {
  admitSocialName: boolean
  isLoading: boolean
}

const getInitialState = (): ConfigState => ({
  admitSocialName: false,
  isLoading: false,
})

interface ConfigProviderProps {
  children: React.ReactNode
  unitId: string | number
}

function ConfigProvider({
  children,
  unitId,
}: ConfigProviderProps): JSX.Element {
  const [state, setState] = useStateWithMerge(getInitialState())

  useEffect(() => {
    async function fetchConfigurations(): Promise<void> {
      if (!unitId || unitId === 0) {
        setState({ admitSocialName: false, isLoading: false })
        return
      }

      setState({ isLoading: true })
      try {
        const data = await API.getConfigurations(unitId)

        if (!isConfigurationResponse(data)) {
          setState({ admitSocialName: false, isLoading: false })
          return
        }

        // Extract admitSocialName from the configurations object
        const pacienteCategory = CONFIG_CATEGORIES.PACIENTE
        const pacienteConfig = data.configurations?.[pacienteCategory]
        const admitSocialNameKey = CONFIG_KEYS.PACIENTE.ADMIT_SOCIAL_NAME
        const admitSocialNameValue = pacienteConfig?.[admitSocialNameKey]
        const admitSocialName = Boolean(admitSocialNameValue ?? false)

        setState({ admitSocialName, isLoading: false })
      } catch (e) {
        // If configuration fetch fails, default to false
        setState({ admitSocialName: false, isLoading: false })
      }
    }

    fetchConfigurations()
  }, [unitId])

  const { admitSocialName, isLoading } = state

  return (
    <ConfigContext.Provider value={{ admitSocialName, isLoading }}>
      {children}
    </ConfigContext.Provider>
  )
}

interface ConfigContextType {
  admitSocialName: boolean
  isLoading: boolean
}

function useConfig(): ConfigContextType {
  const context = useContext(ConfigContext)
  if (!context) {
    if (process.env.NODE_ENV !== `production`) {
      console.warn(
        `useConfig() is being used outside of ConfigProvider. ` +
          `Returning default values. If this is intentional (e.g., in modals), ` +
          `consider passing the config values as props instead.`,
      )
    }
    // Return default values when used outside ConfigProvider (e.g., in modals)
    return { admitSocialName: false, isLoading: false }
  }
  return context
}

export { ConfigProvider, useConfig }
